// What happens when a game ends. ONE function, for every mode.
//
// Six mode screens, Endurance and the Daily all end the same way: record the
// run, add to the one shared total, update the record if it is one, work out
// which trophies that just unlocked, and hand a summary to the Results screen.
// Doing that in each screen is how the shared total quietly stops being shared.

import { addRun, runs, runsReady } from '../store.js';
import { profile, ensureProfile, updateProfile } from './profile.js';
import { awardTrophies } from './trophies.js';
import { setResult } from './results.js';

/**
 * End a run and prepare the Results screen.
 *
 * @param {object} summary
 * @param {string} summary.mode          which mode ended
 * @param {number} summary.score         points earned in this run
 * @param {number} [summary.lastedMs]    how long he survived — the Endurance score
 * @param {number} [summary.correct]
 * @param {number} [summary.wrong]
 * @param {number} [summary.bestChain]   longest unbroken streak inside the run
 * @param {number} [summary.ausBonuses]  how many times the AUS BONUS fired
 * @param {number} [summary.dailyStreak] set by the Daily only
 * @returns the summary the Results screen renders
 */
export async function finishRun(summary) {
  const {
    mode,
    score = 0,
    lastedMs = 0,
    correct = 0,
    wrong = 0,
    bestChain = 0,
    ausBonuses = 0,
  } = summary;

  await ensureProfile();
  await runsReady.catch(() => null);

  const run = addRun({ mode, score, lastedMs, correct, wrong, bestChain, ausBonuses });

  // Endurance is scored in TIME, so the record it beats is a time.
  const previousBest = profile.value.bestEnduranceMs ?? 0;
  const isRecord = mode === 'endurance' && lastedMs > previousBest;

  const patch = { totalScore: (profile.value.totalScore ?? 0) + score };
  if (isRecord) patch.bestEnduranceMs = lastedMs;
  if (summary.dailyStreak !== undefined) {
    patch.dailyStreak = summary.dailyStreak;
    patch.lastDailyOn = summary.lastDailyOn;
  }
  updateProfile(patch);

  const won = await awardTrophies({ run, profile: profile.value, runs: runs.value });

  return setResult({
    mode,
    score,
    lastedMs,
    correct,
    wrong,
    bestChain,
    ausBonuses,
    isRecord,
    previousBest,
    totalScore: profile.value.totalScore ?? 0,
    newTrophies: won.map((t) => ({ id: t.id, name: t.name, icon: t.icon, description: t.description })),
  });
}
